import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
/* eslint-disable no-plusplus */

/* eslint-disable no-bitwise */

import React, { useMemo, useState } from 'react';
import { theme } from "../../theme";
import styled, { css } from 'styled-components';
import { baseTextStyles, textStyles } from "../Text/Text";
import { useHasImageLoaded } from "./useHasImageLoaded";
import { useTimeout } from "../../hooks";
import { getBackgroundColor } from "../../theme/color-tokens";
var _theme$transitions = theme.transitions,
    eases = _theme$transitions.eases,
    durations = _theme$transitions.durations;
export var Avatar = React.forwardRef(function (_ref, forwardedRef) {
  var background = _ref.background,
      children = _ref.children,
      altProp = _ref.alt,
      uid = _ref.uid,
      src = _ref.src,
      srcSet = _ref.srcSet,
      _ref$shouldFadeOnLoad = _ref.shouldFadeOnLoad,
      shouldFadeOnLoadProp = _ref$shouldFadeOnLoad === void 0 ? true : _ref$shouldFadeOnLoad,
      _ref$imgProps = _ref.imgProps,
      imgProps = _ref$imgProps === void 0 ? {} : _ref$imgProps,
      _ref$placeholderBackg = _ref.placeholderBackgroundColor,
      placeholderBackgroundColor = _ref$placeholderBackg === void 0 ? 'component' : _ref$placeholderBackg,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 24 : _ref$size,
      other = _objectWithoutProperties(_ref, ["background", "children", "alt", "uid", "src", "srcSet", "shouldFadeOnLoad", "imgProps", "placeholderBackgroundColor", "size"]);

  var alt = typeof altProp === 'string' ? altProp : '?';
  var initials = children || getInitials(alt);
  var hasLoaded = useHasImageLoaded({
    src: src,
    srcSet: srcSet
  }); // This is a naive strategy to only fade in images
  // if they aren't already cached. Effectively, if it loads
  // within 50ms we treat it as already cached.

  var _useState = useState(true),
      _useState2 = _slicedToArray(_useState, 2),
      cached = _useState2[0],
      setCached = _useState2[1];

  useTimeout(function () {
    return setCached(false);
  }, 50);
  var shouldFade = shouldFadeOnLoadProp && !cached;
  var showLetters = children || !(src || srcSet) || hasLoaded === 'error';

  if (!children && !altProp) {
    showLetters = false;
  }

  var color = useMemo(function () {
    if (background) {
      return getBackgroundColor(background);
    }

    if (children || !altProp && !src) {
      return theme.colors.background.secondary;
    } // If rendering letters, use a color from our theme depending
    // upon the user initials.


    if (showLetters) {
      return getAvatarColor(uid !== null && uid !== void 0 ? uid : alt);
    }

    if (!shouldFadeOnLoadProp) {
      return 'transparent';
    }

    return getBackgroundColor(placeholderBackgroundColor);
  }, [background, children, altProp, src, showLetters, shouldFadeOnLoadProp, placeholderBackgroundColor, uid, alt]);

  var renderChildren = function renderChildren() {
    if (showLetters) {
      return initials;
    }

    if (altProp || src) {
      return /*#__PURE__*/React.createElement(AvatarImage, _extends({}, imgProps, {
        alt: alt,
        src: src,
        srcSet: srcSet
      }));
    }

    return /*#__PURE__*/React.createElement(AnonymousUser, null);
  };

  return /*#__PURE__*/React.createElement(StyledAvatar, _extends({
    $size: size,
    $fade: shouldFade,
    $background: color,
    $loading: !hasLoaded,
    ref: forwardedRef
  }, other), renderChildren());
});
var AvatarImage = styled.img.withConfig({
  displayName: "Avatar__AvatarImage",
  componentId: "vapor__hep5ij-0"
})(["color:transparent;width:100%;height:100%;object-fit:cover;text-align:center;text-indent:10000px;"]);
export var StyledAvatar = styled.div.withConfig({
  displayName: "Avatar__StyledAvatar",
  componentId: "vapor__hep5ij-1"
})(["color:", ";display:flex;align-items:center;overflow:hidden;flex-shrink:0;user-select:none;border-radius:50%;justify-content:center;transition:background-color ", " ", ";background-color:", ";text-transform:uppercase;will-change:transform;", ";", "{transition:", ";opacity:", ";}"], theme.colors.palette.grey10, durations[100], eases.primary, function (p) {
  return p.$background;
}, function (p) {
  return getSizeStyles(p.$size);
}, AvatarImage, function (p) {
  return p.$fade ? "opacity ".concat(durations[200], " ").concat(eases.primary) : "none";
}, function (p) {
  return p.$loading ? 0 : 1;
});
export function getSizeStyles(size) {
  return css(["flex:0 0 ", "px;width:", "px;height:", "px;", ";"], size, size, size, typography(size));
}

function typography(size) {
  switch (size) {
    case 16:
      // A custom font style not used elsewhere
      return css(["", ";font-weight:600;font-size:8px;line-height:16px;"], baseTextStyles);

    case 20:
    case 24:
      return textStyles.caption;

    case 32:
    case 48:
      return textStyles.link;

    case 88:
      return textStyles.header;

    default:
      throw new Error('Avatar is using an unsupported size');
  }
}

function getInitials(name) {
  var _name$split = name.split(' '),
      _name$split2 = _slicedToArray(_name$split, 2),
      firstName = _name$split2[0],
      lastName = _name$split2[1];

  return firstName && lastName ? "".concat(firstName.charAt(0)).concat(lastName.charAt(0)) : firstName.charAt(0);
}

function makeHash(name) {
  var hash = 0;
  if (name.length === 0) return hash;

  for (var i = 0; i < name.length; i++) {
    var chr = name.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }

  return hash;
}

var colors = ['tintOrange', 'tintGold', 'tintYellow', 'tintGreen', 'tintTeal', 'tintSkyBlue', 'tintBlue', 'tintPurple', 'tintPink'];
export function getAvatarColor(id) {
  return theme.colors.palette[colors[Math.abs(makeHash(id)) % colors.length]];
}

var AnonymousUser = function AnonymousUser() {
  return /*#__PURE__*/React.createElement("svg", {
    width: "24px",
    height: "24px",
    "aria-label": "Anonymous user",
    fill: "#fff",
    viewBox: "0 0 128 128",
    role: "img"
  }, /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M103,102.1388 C93.094,111.92 79.3504,118 64.1638,118 C48.8056,118 34.9294,111.768 25,101.7892 L25,95.2 C25,86.8096 31.981,80 40.6,80 L87.4,80 C96.019,80 103,86.8096 103,95.2 L103,102.1388 Z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M63.9961647,24 C51.2938136,24 41,34.2938136 41,46.9961647 C41,59.7061864 51.2938136,70 63.9961647,70 C76.6985159,70 87,59.7061864 87,46.9961647 C87,34.2938136 76.6985159,24 63.9961647,24"
  })));
};